import { useMutation, useQuery, useQueryClient, useSuspenseQuery } from '@tanstack/react-query';
import { getProviderInsurances, createProviderLicense, CreateProviderLicenseInput, deleteProviderLicense, getMentalHealthRoles, getProviderLicenses, ProviderInsurance, patchLicense, LicenseInput, getProviderMissingPayers, getProviderLicensesCollaborativeAgreements, createProviderLicenseCollabAgreement, CreateProviderLicenseCollabAgreementInput, patchProviderLicenseCollabAgreement, PatchProviderLicenseCollabAgreementInput, ReviewProviderLicenseCollabAgreementInput, reviewProviderLicenseCollabAgreement, RejectProviderLicenseCollabAgreementInput, rejectProviderLicenseCollabAgreement, archiveProviderLicenseCollabAgreement, getProviderSignedCollaborativeAgreements, CollaborativeAgreementSigned, startProviderLicenseGoLive } from '@uplift/api';
import { BaseQueryOpts, BaseSuspenseQueryOpts } from './baseTypes';
import groupBy from 'lodash/groupBy';
import { providerQueryKeys } from './provider';


export const useMentalHealthRolesQuery = (opts?: BaseQueryOpts) => {
  return useQuery({
    queryKey: [ 'mentalHealthRoles' ],
    queryFn: getMentalHealthRoles,
    select: response => response.data,
    ...opts,
  });
};


export const useCreateProviderLicenseMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ providerId, ...args }: CreateProviderLicenseInput & { providerId: string }) => {
      return await createProviderLicense(providerId, args);
    },
    onSuccess: async (_, { providerId }) => {
      await Promise.all([
        queryClient.invalidateQueries(providerQueryKeys.licenses(providerId)),
        queryClient.invalidateQueries(providerQueryKeys.insurances(providerId)),
      ]);
    },
  });
};


export const useUpdateProviderLicenseMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ providerId, licenseId, ...args }: LicenseInput & { providerId: string; licenseId: string }) => {
      return await patchLicense(providerId, licenseId, args);
    },
    onSuccess: async (_, { providerId }) => {
      await queryClient.invalidateQueries(providerQueryKeys.licenses(providerId));
    },
  });
};


export const useProviderLicensesQuery = (providerId: string, opts?: BaseQueryOpts) => {
  return useQuery({
    queryKey: providerQueryKeys.licenses(providerId),
    queryFn: async () => getProviderLicenses(providerId),
    select: response => response.data,
    ...opts,
  });
};

export const useProviderLicensesCollabAgreementsQuery = (providerId: string, opts?: BaseQueryOpts) => {
  return useQuery({
    queryKey: providerQueryKeys.licensesCollaborativeAgreements(providerId),
    queryFn: async () => getProviderLicensesCollaborativeAgreements(providerId),
    select: response => response.data,
    ...opts,
  });
};

export const useProviderSignedCollaborativeAgreementsQuery = (providerId: string, opts?: BaseQueryOpts) => {
  return useQuery({
    queryKey: providerQueryKeys.signedCollaborativeAgreements(providerId),
    queryFn: async () => getProviderSignedCollaborativeAgreements(providerId),
    select: response => response.data.reduce((map, agreement) => ({ ...map, [agreement.collaborativeMatchId]: agreement }), {} as Record<string, CollaborativeAgreementSigned>),
    ...opts,
  });
};

export const useProviderLicensesCollabAgreementsForLicenseQuery = (providerId: string, licenseId: string, opts?: BaseSuspenseQueryOpts) => {
  return useSuspenseQuery({
    queryKey: providerQueryKeys.licensesCollaborativeAgreements(providerId),
    queryFn: async () => getProviderLicensesCollaborativeAgreements(providerId),
    select: response => response.data?.[licenseId] || [],
    ...opts,
  });
};

export const useProviderLicensesActiveCollaborativeAgreementsForLicenseQuery = (providerId: string, licenseId: string, opts?: BaseSuspenseQueryOpts) => {
  return useSuspenseQuery({
    queryKey: providerQueryKeys.licensesCollaborativeAgreements(providerId),
    queryFn: async () => getProviderLicensesCollaborativeAgreements(providerId),
    select: response => (response.data?.[licenseId] ?? []).find(a => a.archivedAt === null),
    ...opts,
  });
};


export const useCreateProviderLicenseCollabAgreementMutation = (providerId: string, licenseId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (args: {
      providerId: string;
      licenseId: string;
      data: CreateProviderLicenseCollabAgreementInput;
    }) => {
      return await createProviderLicenseCollabAgreement(providerId, licenseId, args.data);
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: providerQueryKeys.licensesCollaborativeAgreements(providerId) });
    }
  });
};


export const usePatchProviderLicenseCollabAgreementMutation = (providerId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (args: {
      collabAgreementId: string;
      data: PatchProviderLicenseCollabAgreementInput;
    }) => {
      return await patchProviderLicenseCollabAgreement(args.collabAgreementId, args.data);
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: providerQueryKeys.licensesCollaborativeAgreements(providerId) });
    }
  });
};


export const useReviewProviderLicenseCollabAgreementMutation = (providerId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (args: {
      collabAgreementId: string;
      data: ReviewProviderLicenseCollabAgreementInput;
    }) => {
      return await reviewProviderLicenseCollabAgreement(args.collabAgreementId, args.data);
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: providerQueryKeys.licensesCollaborativeAgreements(providerId) });
    }
  });
};


export const useRejectProviderLicenseCollabAgreementMutation = (providerId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (args: {
      collabAgreementId: string;
      data: RejectProviderLicenseCollabAgreementInput;
    }) => {
      return await rejectProviderLicenseCollabAgreement(args.collabAgreementId, args.data);
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: providerQueryKeys.licensesCollaborativeAgreements(providerId) });
    }
  });
};


export const useArchiveProviderLicenseCollabAgreementMutation = (providerId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (args: {
      collabAgreementId: string;
    }) => {
      return await archiveProviderLicenseCollabAgreement(args.collabAgreementId);
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: providerQueryKeys.licensesCollaborativeAgreements(providerId) });
    }
  });
};


export const useProviderMissingPayersQuery = (providerId: string, opts?: BaseQueryOpts) => {
  return useQuery({
    queryKey: providerQueryKeys.missingPayers(providerId),
    queryFn: async () => getProviderMissingPayers(providerId),
    select: response => response.data,
    ...opts,
  });
};


export const useDeleteProviderLicenseMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ providerId, licenseId }: { providerId: string; licenseId: string }) => {
      return await deleteProviderLicense(providerId, licenseId);
    },
    onSuccess: async (_, { providerId }) => {
      await queryClient.invalidateQueries(providerQueryKeys.licenses(providerId));
    },
  });
};


export const useStartProviderLicenseGoLiveMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ providerId, licenseId }: { providerId: string; licenseId: string }) => {
      return await startProviderLicenseGoLive(providerId, licenseId);
    },
    onSuccess: async (_, { providerId }) => {
      await queryClient.invalidateQueries(providerQueryKeys.licenses(providerId));
    },
  });
};


export const useProviderInsurancesQuery = (providerId: string, opts?: BaseQueryOpts) => {
  return useQuery({
    queryKey: [ 'user', providerId, 'insurances' ],
    queryFn: async () => getProviderInsurances(providerId),
    select: response => groupBy(response.data, 'stateId') as Record<string, ProviderInsurance[]>,
    ...opts,
  });
};
